<template>
	<div
		v-if="items.length"
		class="c-form-dropdown"
		:class="{
			'c-form-dropdown--is-expanded': isExpanded,
		}"
	>
		<DropdownButton
			:id="computedId"
			ref="button"
			v-bind="$attrs"
			v-model="internalValue"
			:aria-labelledby="
				[computedLabelId, computedId].filter(Boolean).join(' ')
			"
			:aria-owns="`c-form-dropdown__list-${_uid} c-form-dropdown__button-up-${_uid} c-form-dropdown__button-down-${_uid}`"
			:aria-required="String(required)"
			class="
				flex-shrink flex-grow flex
				justify-start
				items-center
				text-left
				w-full
				h-full
				py-12
			"
			:class="{
				'bg-white text-theme rounder-t-16': isExpanded,
			}"
			@state:toggle="onStateToggle"
			v-on="computedListeners"
		>
			<span v-text="computedPlaceholder"></span>

			<SvgIconWedge
				:class="[
					'flex-shrink-0 flex-grow-0',
					'ml-auto w-12 h-8 mt-1',
					{ 'transform -rotate-180 -mt-1': isExpanded },
				]"
			/>
		</DropdownButton>
		<input type="hidden" :name="name" :value="String(internalValue)" />

		<span
			v-if="computedLabelId"
			:id="computedLabelId"
			class="hidden"
			v-text="label"
		></span>

		<div
			ref="scrollShade"
			class="
				c-form-dropdown__list-wrap
				absolute
				top-full
				left-0
				right-0
				rounded-b-30
				overflow-hidden
			"
			:style="{
				'--scroll-shade-alpha-top': scrollShadeAlphaTop,
				'--scroll-shade-alpha-bottom': scrollShadeAlphaBottom,
			}"
		>
			<DropdownOptionList
				:id="`c-form-dropdown__list-${_uid}`"
				v-slot="{ activedescendant, isNavigatingByKeyboard }"
				class="
					relative
					pb-2xs/v
					bg-white
					text-theme text-body-sm
					rounded-b-30
					overflow-auto
					overscroll-auto
				"
				:style="{
					maxHeight: maxHeight
						? `min(${maxHeight}px, 422px, 50vh)`
						: 'min(422px, 50vh)',
				}"
			>
				<DropdownOption
					v-for="(item, itemIndex) in items.filter(
						(item) => item.value
					)"
					:id="`c-form-dropdown__option-${_uid}-${item.value}`"
					:key="`${_uid}-${item.value}`"
					:value="item.value"
					class="flex items-center px-3xs/h py-5xs/v snap-my-xs/v"
					:class="{
						'>=768:mt-3xs/v': showIcon && itemIndex,
						'hover:bg-theme-20 hover:bg-opacity-20': !(
							isNavigatingByKeyboard &&
							`c-form-dropdown__option-${_uid}-${item.value}` ===
								activedescendant
						),
						'bg-theme text-white hover:bg-theme-90':
							isNavigatingByKeyboard &&
							`c-form-dropdown__option-${_uid}-${item.value}` ===
								activedescendant,
					}"
				>
					<template v-if="showIcon">
						<AreaIcon
							class="
								flex-shrink-0 flex-grow-0
								w-30
								h-30
								mr-16
								>=768:mr-20
							"
							:src="item.iconUrl"
							:invert="
								!!(
									isNavigatingByKeyboard &&
									`c-form-dropdown__option-${_uid}-${item.value}` ===
										activedescendant
								)
							"
						/>
					</template>

					<span>{{ item.label }}</span>
				</DropdownOption>
			</DropdownOptionList>

			<!-- Arrow icons -->
			<TransitionExt name="fade-in-out" duration="200">
				<button
					v-if="scrollShadeAlphaTop"
					:id="`c-form-dropdown__button-up-${_uid}`"
					class="
						absolute
						z-10
						top-0
						left-1/2
						w-52
						h-52
						transform
						-translate-x-1/2
						flex
						justify-center
						items-center
						transition
						duration-200
						ease-in-out
						bg-theme-20
						hover:bg-theme-10
						rounded-full
					"
					@mousedown="startScrollUp"
					@touchstart="startScrollUp"
				>
					<SvgIconWedge
						class="transform rotate-180 text-gray-darker w-16 h-10"
					/>
				</button>
			</TransitionExt>
			<TransitionExt name="fade-in-out" duration="200">
				<button
					v-if="scrollShadeAlphaBottom"
					:id="`c-form-dropdown__button-down-${_uid}`"
					class="
						absolute
						z-10
						bottom-16
						left-1/2
						w-52
						h-52
						transform
						-translate-x-1/2
						flex
						justify-center
						items-center
						transition
						duration-200
						ease-in-out
						bg-theme-20
						hover:bg-theme-10
						rounded-full
					"
					@mousedown="startScrollDown"
					@touchstart="startScrollDown"
				>
					<SvgIconWedge class="text-gray-darker w-16 h-10" />
				</button>
			</TransitionExt>
		</div>
	</div>
</template>

<script>
import {
	DropdownButton,
	DropdownOption,
	DropdownOptionList,
} from '~/components/shared/Dropdown';
import AreaIcon from '~/components/main/AreaIcon';
import SvgIconWedge from '~/assets/svgs/icon-wedge.svg?inline';

export default {
	name: 'FormDropdown',

	components: {
		DropdownButton,
		DropdownOption,
		DropdownOptionList,
		AreaIcon,
		SvgIconWedge,
	},
	inheritAttrs: false,

	props: {
		id: {
			type: String,
			default: '',
		},

		type: {
			type: String,
			default: 'dropdown',
		},

		name: {
			type: String,
			default: '',
		},

		value: {
			type: String,
			default: '',
		},

		required: {
			type: Boolean,
			default: false,
		},

		items: {
			type: Array,
			default: () => [],
		},

		label: {
			type: String,
			default: '',
		},

		placeholder: {
			type: String,
			default: '',
		},

		showIcon: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			isExpanded: false,
			internalValue: this.value,

			resizeObserver: null,
			animationFrame: null,
			scrollShadeAlphaTop: 0,
			scrollShadeAlphaBottom: 0,
			maxHeight: null,
		};
	},

	computed: {
		computedId() {
			return this.id || `c-form-dropdown-${this._uid}`;
		},

		computedLabelId() {
			if (!this.internalValue && this.label === this.placeholder) {
				return null;
			}
			return this.label ? `${this.computedId}__label` : null;
		},

		computedListeners() {
			const listeners = {
				...this.$listeners,
			};
			delete listeners.input;
			return listeners;
		},

		computedPlaceholder() {
			return (
				this.items.find((item) => item.value === this.internalValue)
					?.label ||
				this.items.find((item) => !item.value)?.label ||
				this.placeholder ||
				''
			);
		},
	},

	watch: {
		value(value) {
			this.internalValue = value;
			this.updateCheckedItems();
		},
		internalValue(value) {
			this.$emit('input', value);
			this.updateCheckedItems();
		},
	},

	mounted() {
		// Detect if a scroll shadow should be present
		const { scrollShade } = this.$refs;
		if (scrollShade) {
			this.resizeObserver = new ResizeObserver(() => {
				this.updateScrollShade();
			});
			this.resizeObserver.observe(scrollShade);
			scrollShade.addEventListener(
				'scroll',
				this.updateScrollShade,
				true
			);
		}

		// Update max height
		this.updateMaxHeight();
		window.addEventListener('resize', this.updateMaxHeight);
	},

	beforeDestroy() {
		const { scrollShade } = this.$refs;
		this.resizeObserver?.disconnect?.();
		scrollShade?.removeEventListener?.(
			'scroll',
			this.updateScrollShade,
			true
		);

		window.removeEventListener('resize', this.updateMaxHeight);
	},

	methods: {
		updateCheckedItems() {
			this.items.forEach((item) => {
				this.$set(item, 'checked', false);
			});
			this.internalValue.split(',').forEach((value) => {
				const item = this.items.find((item) => item.value === value);
				if (item) {
					this.$set(item, 'checked', true);
				}
			});
		},
		updateScrollShade() {
			window.cancelAnimationFrame(this.animationFrame);
			this.animationFrame = window.requestAnimationFrame(() => {
				const { scrollShade } = this.$refs;
				const container = scrollShade?.firstChild;
				if (container) {
					const { scrollTop, scrollHeight, clientHeight } = container;
					const scrollBottom =
						scrollHeight - clientHeight - scrollTop;
					const scrollShadeAlphaTop = Math.min(
						1,
						Math.max(0, scrollTop / 60)
					);
					const scrollShadeAlphaBottom = Math.min(
						1,
						Math.max(0, scrollBottom / 60)
					);
					if (
						this.scrollShadeAlphaTop !== scrollShadeAlphaTop ||
						this.scrollShadeAlphaBottom !== scrollShadeAlphaBottom
					) {
						this.scrollShadeAlphaTop = scrollShadeAlphaTop;
						this.scrollShadeAlphaBottom = scrollShadeAlphaBottom;
					}
				}
			});
		},
		onStateToggle(event) {
			this.isExpanded = event;
			if (event) {
				this.$el.scrollIntoView();
			}
		},

		updateMaxHeight() {
			const formEl = this.$el?.closest?.('form');
			this.maxHeight = null;
			if (formEl) {
				const formRect = formEl.getBoundingClientRect();
				const rect = this.$el.getBoundingClientRect();
				this.maxHeight = formRect.bottom - rect.top + 120;
			}
		},

		startScrollUp() {
			const scrollAmount = 10;
			const list = this.$el.querySelector(
				`#c-form-dropdown__list-${this._uid}`
			);
			let scrollMultiplier = 1;
			let scrolls = 0;

			let isScrolling = true;
			const stopScroll = (e) => {
				isScrolling = false;
				window.removeEventListener('mouseup', stopScroll, true);
				window.removeEventListener('touchend', stopScroll, true);

				if (e && scrolls < 5 && list) {
					list.scrollTop -= Math.max(
						0,
						list.offsetHeight - 150 - scrolls * scrollAmount
					);
				}
			};
			window.addEventListener('mouseup', stopScroll, true);
			window.addEventListener('touchend', stopScroll, true);

			const scrollUp = () => {
				if (!this.scrollShadeAlphaTop || !list) {
					stopScroll();
				}

				if (isScrolling) {
					scrolls++;
					list.scrollTop -= scrollAmount * scrollMultiplier;

					scrollMultiplier = Math.min(10, scrollMultiplier * 1.02);
					window.setTimeout(scrollUp, 30);
				}
			};
			scrollUp();
		},
		startScrollDown() {
			const scrollAmount = 10;
			const list = this.$el.querySelector(
				`#c-form-dropdown__list-${this._uid}`
			);
			let scrollMultiplier = 1;
			let scrolls = 0;

			let isScrolling = true;
			const stopScroll = (e) => {
				isScrolling = false;
				window.removeEventListener('mouseup', stopScroll, true);
				window.removeEventListener('touchend', stopScroll, true);

				if (e && scrolls < 5 && list) {
					list.scrollTop += Math.max(
						0,
						list.offsetHeight - 150 - scrolls * scrollAmount
					);
				}
			};
			window.addEventListener('mouseup', stopScroll, true);
			window.addEventListener('touchend', stopScroll, true);

			const scrollDown = () => {
				if (!this.scrollShadeAlphaBottom || !list) {
					stopScroll();
				}

				if (isScrolling) {
					scrolls++;
					list.scrollTop += scrollAmount * scrollMultiplier;

					scrollMultiplier = Math.min(10, scrollMultiplier * 1.02);
					window.setTimeout(scrollDown, 30);
				}
			};
			scrollDown();
		},
	},
};
</script>

<style lang="postcss">
.c-form-dropdown {
	@apply relative w-full h-auto text-input-form text-white border-b border-white;
}
.c-form-dropdown--is-expanded {
	@apply z-10;
	width: calc(
		100% +
			var(
				--theme-horizontalSpacing-3xs-h,
				var(--theme-horizontalSpacing-3xs-h--sm)
			) * 2
	);
	margin-inline: calc(
		var(
				--theme-horizontalSpacing-3xs-h,
				var(--theme-horizontalSpacing-3xs-h--sm)
			) * -1
	);
	filter: drop-shadow(0px 4px 24px rgba(var(--theme-colors-theme), 0.16));

	& > button {
		@apply px-3xs/h;
	}
}

.c-form-dropdown__list-wrap {
	&:after {
		@apply rounded-b-30;
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(
				rgba(255, 255, 255, var(--scroll-shade-alpha-top, 0)) 24%,
				rgba(255, 255, 255, 0)
			),
			linear-gradient(
					rgba(255, 255, 255, 0),
					rgba(255, 255, 255, var(--scroll-shade-alpha-bottom, 0)) 76%
				)
				0 100%;
		background-repeat: no-repeat;
		background-attachment: local, local;
		background-size: 100% 60px, 100% 60px;
		pointer-events: none;
	}
}
</style>
